import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Rocket, ExternalLink, Sparkles, ShieldAlert } from 'lucide-react';
import { useDemo } from '../context/DemoContext';

const PREVIEW_MODE = process.env.REACT_APP_PREVIEW_MODE === 'true';
const STORAGE_KEY = 'gfg_preview_gate_dismissed_v1';

/**
 * PreviewGate — full-screen splash the very first time a visitor lands on
 * the site while REACT_APP_PREVIEW_MODE=true. Explains that the app is in
 * preview (token trades on Pump.fun, gameplay is demo-only), then enters
 * demo mode automatically when the visitor clicks ENTER.
 *
 * Dismissed once per browser via localStorage. Admin-access sessions
 * (already logged in via /admin-access) bypass the gate entirely.
 */
const PreviewGate = () => {
  const navigate = useNavigate();
  const { enterDemo, isDemo } = useDemo();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!PREVIEW_MODE) return;
    if (typeof window === 'undefined') return;
    const dismissed = window.localStorage.getItem(STORAGE_KEY) === 'true';
    const adminAccess = window.sessionStorage.getItem('gfg_admin_access') === 'true';
    if (dismissed || adminAccess || isDemo) return;
    setVisible(true);
  }, [isDemo]);

  const enter = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, 'true');
      window.sessionStorage.setItem('gfg_demo', 'true');
    } catch { /* ignore */ }
    enterDemo?.();
    setVisible(false);
  };

  const goFairLaunch = () => {
    enter();
    setTimeout(() => navigate('/fair-launch'), 40);
  };

  if (!PREVIEW_MODE) return null;

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          data-testid="preview-gate"
          style={{ background: 'rgba(4,4,4,0.94)', backdropFilter: 'blur(14px)' }}
        >
          {/* Ambient — same eerie red/gold as the rest of the app */}
          <div className="absolute inset-0 pointer-events-none">
            <motion.div className="absolute inset-0"
              style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(239,68,68,0.08) 0%, transparent 55%)' }}
              animate={{ opacity: [0.5, 0.9, 0.5] }} transition={{ duration: 5, repeat: Infinity }} />
            <motion.div className="absolute inset-0"
              style={{ background: 'radial-gradient(ellipse at 50% 70%, rgba(234,179,8,0.05) 0%, transparent 45%)' }}
              animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 6, repeat: Infinity, delay: 1 }} />
          </div>
          {/* CRT scanlines */}
          <div className="absolute inset-0 pointer-events-none mix-blend-overlay" style={{
            background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.15) 50%)',
            backgroundSize: '100% 4px', opacity: 0.12,
          }} />

          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, type: 'spring', stiffness: 120 }}
            className="relative w-full max-w-xl overflow-hidden"
            style={{
              background: 'rgba(10,10,10,0.92)',
              border: '1px solid rgba(239,68,68,0.35)',
              boxShadow: '0 0 40px rgba(239,68,68,0.15), inset 0 0 20px rgba(255,255,255,0.02)',
            }}
          >
            {/* Corner brackets */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l" style={{ borderColor: 'rgba(239,68,68,0.35)' }} />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r" style={{ borderColor: 'rgba(239,68,68,0.35)' }} />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l" style={{ borderColor: 'rgba(239,68,68,0.35)' }} />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r" style={{ borderColor: 'rgba(239,68,68,0.35)' }} />

            <div className="px-6 md:px-10 py-8 md:py-10 relative">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-4 h-4" style={{ color: '#eab308', filter: 'drop-shadow(0 0 4px rgba(234,179,8,0.6))' }} />
                <p className="text-[10px] uppercase tracking-[0.35em]"
                   style={{ fontFamily: "'Cinzel', serif", color: 'rgba(234,179,8,0.8)' }}>
                   Preview Mode Active
                </p>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4"
                  style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}>
                <span style={{
                  background: 'linear-gradient(180deg, #ffffff 0%, #e5e5e5 30%, #a3a3a3 70%, #737373 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 12px rgba(239,68,68,0.15))',
                }} data-testid="preview-gate-title">The Sewers Are Sealed.</span>
              </h1>

              <p className="text-sm leading-relaxed mb-5"
                 style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.65)' }}>
                <span style={{ color: '#eab308' }}>$GFG</span> launches on <strong>Pump.fun</strong> — public, permissionless, no allocation.
                The full gameplay economy (real leaderboards, real payouts) unlocks after the audit in a few months.
              </p>
              <p className="text-xs leading-relaxed mb-6"
                 style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.4)' }}>
                Right now: play every game in <span style={{ color: '#ef4444' }}>preview mode</span> — no wallet required, no real fees, all 10 Descent floors playable. Creator rewards from Pump.fun are already accruing on the public dev wallet. When the real launch drops, top preview-era players get first access.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6 text-[10px]"
                   style={{ fontFamily: "'Courier New', monospace" }}>
                {[
                  { icon: Rocket, tint: '#eab308', label: 'Token on Pump.fun', sub: 'Real, tradeable' },
                  { icon: Sparkles, tint: '#a855f7', label: 'App in Preview', sub: 'Demo-only gameplay' },
                  { icon: ShieldAlert, tint: '#10b981', label: 'Real launch soon', sub: 'Audit in progress' },
                ].map((c) => (
                  <div key={c.label} className="p-2 flex items-center gap-2"
                       style={{ background: `${c.tint}08`, border: `1px solid ${c.tint}22` }}>
                    <c.icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: c.tint }} />
                    <div className="min-w-0">
                      <p style={{ color: 'rgba(255,255,255,0.7)' }}>{c.label}</p>
                      <p style={{ color: 'rgba(255,255,255,0.35)' }} className="text-[9px]">{c.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <motion.button onClick={enter}
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wider transition"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    background: 'linear-gradient(180deg, rgba(239,68,68,0.25), rgba(239,68,68,0.12))',
                    border: '1px solid rgba(239,68,68,0.5)',
                    color: '#fff',
                    boxShadow: '0 0 20px rgba(239,68,68,0.15)',
                  }}
                  data-testid="preview-gate-enter">
                  Enter Preview <span aria-hidden>›</span>
                </motion.button>
                <motion.button onClick={goFairLaunch}
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wider transition"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    background: 'rgba(234,179,8,0.1)',
                    border: '1px solid rgba(234,179,8,0.35)',
                    color: '#eab308',
                  }}
                  data-testid="preview-gate-fair-launch">
                  Fair Launch <ExternalLink className="w-3.5 h-3.5" />
                </motion.button>
              </div>

              <p className="mt-5 text-center text-[9px] tracking-[0.15em] uppercase"
                 style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.2)' }}>
                Preview scores don&apos;t count for real payouts · dismissable
              </p>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default PreviewGate;
